/**
 * The app's periods: rolling windows ending now. Every "today / this week /
 * this month" stat (trending, rankings, the leaderboard) is the total over
 * one of these windows. The keys are the `programmes.trends` option values.
 */
export const PERIODS = { today: '1d', week: '7d', month: '30d' } as const
export type Period = keyof typeof PERIODS

export function isPeriod(value: string | null | undefined): value is Period {
    return !!value && Object.prototype.hasOwnProperty.call(PERIODS, value)
}

/** The window profile, coach and programme stats cover. */
export const STATS_WINDOW = '90d'

/** The days a window spans ("7d" is 7). */
export function windowDays(window: string): number {
    return parseInt(window, 10)
}

/** The windows a quiet list widens through, narrowest first. */
const WINDOWS = ['1d', '7d', '30d', STATS_WINDOW] as const

/**
 * Fewest people a list shows before it widens its window: a young platform
 * has quiet days, and a list of one or two reads as broken.
 */
export const MIN_SHOWN = 5

/**
 * `load` over `window`, or over the narrowest wider window that is
 * `enough`, or the widest one; `widened` when it is not `window`. The asked
 * window is read first (usually enough); the wider ones are then read
 * together.
 */
export async function widen<T>(
    window: string,
    load: (window: string) => Promise<T>,
    enough: (value: T) => boolean
): Promise<{ window: string; value: T; widened: boolean }> {
    const first = await load(window)
    const wider = WINDOWS.filter((w) => windowDays(w) > windowDays(window))
    if (enough(first) || wider.length === 0) {
        return { window, value: first, widened: false }
    }
    const values = await Promise.all(wider.map(load))
    const at = values.findIndex(enough)
    const i = at < 0 ? values.length - 1 : at
    return { window: wider[i], value: values[i], widened: true }
}
